import React, { useState } from 'react';
import { useToastUpdater } from '../../context/toastContext/toastContext';
import { useAuthUpdater } from '../../lib/auth/context/authContext';
import useAuthSession from '../../lib/auth/hooks/useAuthSession';
import userService from '../../services/user.service';

import Modal from '../common/Modals/Modal/Modal';
import ScreenSlider from './ScreenSlider/ScreenSlider';

interface Props {
    show?: boolean; // storybook-only override
    initialSlide?: number; // storybook-only override
}

// Shows the one-time onboarding flow on a user's first authenticated visit.
// "Seen" is tracked by the persisted per-application flag config.hub.welcome_intro_seen,
// which is set once the user completes the flow. The flag lives in the session cookie
// (read synchronously on mount), so it stays stable across AppLayout's per-route re-mounts.
const WelcomeIntroductionModal = ({ show, initialSlide }: Props) => {
    const { session } = useAuthSession({ required: true });
    const { refreshSession } = useAuthUpdater();
    const { addToast } = useToastUpdater();

    const hasSeenIntro = session?.user?.config?.hub?.welcome_intro_seen;

    // Checked once on mount; controlled locally thereafter.
    const [showModal, setShowModal] = useState(show || !hasSeenIntro);

    // SSO users authenticate via their organisation's IdP - they have no
    // Vergo password to force a change on, so that slide is skipped.
    const skipPasswordSlide = session?.user?.lastLoginMethod === 'sso';

    const initialFocusRef = React.useRef(null);
    const isPersistingRef = React.useRef(false);

    // Guards against a rapid double-activation of the completion control firing
    // the persist (and refreshSession) twice.
    const handleClose = async () => {
        if (isPersistingRef.current) return;
        isPersistingRef.current = true;

        try {
            // Read-modify-write: the auth API replaces the whole hub blob, so we must
            // resend existing preferences alongside the new flag.
            await userService.updateUserConfig({
                ...session?.user?.config?.hub,
                welcome_intro_seen: true,
            });
            await refreshSession();
        } catch {
            addToast("We couldn't save your welcome progress - you may see this again next time.", 'error', '');
        } finally {
            setShowModal(false);
        }
    };

    return (
        <Modal show={showModal} full overflowYAuto={false} initialFocusRef={initialFocusRef}>
            <div className="rounded drop-shadow-md bg-white w-[100dvw] h-[100dvh] max-w-7xl">
                <div className="h-full flex">
                    <div
                        className="flex-shrink-0 flex-grow-0 basis-1/4 h-full bg-no-repeat bg-cover place-items-center hidden md:grid md:p-2"
                        style={{ backgroundImage: "url('/images/bg-login.jpg')" }}
                    >
                        <img
                            className="w-full max-w-[12rem]"
                            src="/images/illustration_checked_list.svg"
                            alt="A illustrated computer with a checked list"
                        />
                    </div>
                    <div ref={initialFocusRef} className="flex-grow flex flex-col gap-1.5 align-middle justify-center">
                        <ScreenSlider
                            key={`slider-${initialSlide}`}
                            initialSlide={initialSlide}
                            onClose={handleClose}
                            skipPasswordSlide={skipPasswordSlide}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default WelcomeIntroductionModal;
